import styles from "@styles/Root.module.css";

import React, { ConsumerProps, createRef, RefObject } from "react";

type RootProps = {
    /** Include scroll offsets */
    scroll?: boolean;
    /** For single page designs: Fixes the `vw` unit to account for vertical scrollbar and the `vh` unit to account for the browser's URL bar. Exposes inner window size as CSS variables `--pageWidth` and `--pageHeight`, as replacement for `vw` and `vh`. Usage: `width: calc(var(--pageWidth) * ${percent}); height: calc(var(--pageHeight) * ${percent});` */
    singlePage?: boolean;
    /** 
     * For dynamically scaled pages: 
     * Overrides the `rem` unit to apply a scale to CSS styles, as a drop-in replacement for `px` units. 
     * The scale is also available as CSS variable `--scale`. 
     * Arguments are reference width and height. Viewports smaller than these sizes will result in a scale < 1, bigger > 1.
     * If `mobile` is `true`, width and height are flipped, or if 4 arguments are provided, size 3 and 4 are used.
     */
    dynamic?: [width: number, height: number] | [desktopWidth: number, desktopHeight: number, mobileWidth: number, mobileHeight: number];
    /** 
     * Conditionally style the page for mobile. Adds the `mobile` class to root if mobile style. 
     * 
     * Behaviour depends on supplied type:
     * - `function`: return whether the page is mobile for the given `pageWidth` and `pageHeight`. 
     * - `number`: specify at what aspect ratio the page switches style (< ratio is mobile, >= ratio is desktop). 
     * - `boolean`: set the mobile state 
     */
    mobile?: number | boolean | ((pageWidth: number, pageHeight: number) => boolean);
} & ConsumerProps<RootState>;

export type DeviceProps = { mobile: boolean; scale: number; }

type RootState = {
    left: number;
    top: number;
    width: number;
    height: number;
    scale: number;
    ref: RefObject<HTMLDivElement>;
    pageWidth: number;
    pageHeight: number;
    recompute: () => void;
} & DeviceProps;

export class Root extends React.Component<RootProps, RootState> {
    private _ref: RefObject<HTMLDivElement>;
    private _root: HTMLElement;
    private _updateBounds: () => void;

    constructor(props: RootProps) {
        super(props);
        this._ref = createRef();
        this._root = null!;
        this._updateBounds = this.updateBounds.bind(this);

        this.state = {
            left: 0,
            top: 0,
            width: 1,
            height: 1,
            scale: 1,
            ref: this._ref,
            pageWidth: 1,
            pageHeight: 1,
            mobile: false,
            recompute: this._updateBounds
        }
    }

    updateBounds() {
        if (!this._ref.current) return;
        const bounds = this._ref.current.getBoundingClientRect();
        if (this.props.scroll) {
            bounds.x += this._root.scrollLeft;
            bounds.y += this._root.scrollTop;
        }

        let isMobile = false;
        switch (typeof this.props.mobile) {
            case "function": isMobile = this.props.mobile(window.innerWidth, window.innerHeight); break;
            case "number": isMobile = window.innerWidth / window.innerHeight < this.props.mobile; break;
            default: case "boolean": isMobile = this.props.mobile === true; break;
        }

        if (this.props.singlePage) {
            this._ref.current.style.setProperty("--page-width", `${document.body.clientWidth}px`);
            this._ref.current.style.setProperty("--page-height", `${window.innerHeight}px`);
        }

        let scale = 1;
        if (this.props.dynamic) {
            let width = 1, height = 1;
            if (isMobile) {
                if (this.props.dynamic.length === 4) {
                    width = this.props.dynamic[2];
                    height = this.props.dynamic[3];
                } else {
                    width = this.props.dynamic[1];
                    height = this.props.dynamic[0];
                }
            } else {
                width = this.props.dynamic[0];
                height = this.props.dynamic[1];
            }
            scale = Math.min(window.innerWidth / width, window.innerHeight / height);
            document.documentElement.style.fontSize = `${scale}px`;
            this._ref.current.style.setProperty("--scale", `${scale}`);
        }

        this.setState({
            left: bounds.x,
            top: bounds.y,
            width: bounds.width,
            height: bounds.height,
            scale: scale,
            pageWidth: document.body.clientWidth, // Exclude vertical scrollbar
            pageHeight: window.innerHeight,
            mobile: isMobile
        });
    }

    componentDidMount(): void {
        this._root = document.getElementById("__next")!;
        this.updateBounds();
        window.addEventListener("resize", this._updateBounds);
        window.addEventListener("load", this._updateBounds);
        if (this.props.scroll) this._root.addEventListener("scroll", this._updateBounds);
    }

    componentWillUnmount(): void {
        window.removeEventListener("resize", this._updateBounds);
        window.removeEventListener("load", this._updateBounds);
        this._root?.removeEventListener("scroll", this._updateBounds);
    }

    render() {
        const classes = [styles.root];
        if (this.state.mobile) classes.push("mobile");
        return (
            <div ref={this._ref} className={classes.join(" ")}>
                {this.props.children(this.state)}
            </div>
        );
    }
}